/**
 * ============================================================================
 * EMI CALCULATION — minimum standalone utility (Phase 1 correction scope)
 * ============================================================================
 *
 * This is deliberately NOT part of the Phase 2 calculation engine
 * (src/lib/calc/engine.ts), which remains unimplemented. This file exists
 * only to support one specific Phase 1 form requirement: auto-calculating
 * "Original EMI" from Approved Amount + Original Rate + Original Tenure
 * on the loan setup form, so the user doesn't have to compute or manually
 * enter it for a draft/projected loan.
 *
 * It implements the single, well-known, standard reducing-balance EMI
 * formula — the same one already documented/used in this project's
 * earlier Excel-prototype phase. It does NOT implement daily accrual,
 * staged disbursement, rate-splitting, or anything else reserved for
 * Phase 2's real engine. It also does not apply any LIC-specific
 * rounding assumption beyond what Phase 1 already defines on the Loan
 * record (`emiRoundingRule`) — see `applyEmiRounding` below, which reuses
 * that existing, already-configurable field rather than inventing a new
 * one.
 */

import type { EmiRoundingRule } from '../../types';

/**
 * Standard EMI formula: EMI = P × r × (1+r)^n / ((1+r)^n − 1)
 * where P = principal, r = monthly interest rate (annual/12/100), n =
 * tenure in months.
 *
 * Returns `undefined` if the inputs aren't a valid, calculable loan
 * (non-positive principal, non-positive tenure, or a non-finite/negative
 * rate) — callers should treat `undefined` as "not enough information
 * yet to calculate", not as zero.
 */
export function calculateEmi(
  principal: number,
  annualRatePercent: number,
  tenureMonths: number
): number | undefined {
  if (!Number.isFinite(principal) || principal <= 0) return undefined;
  if (!Number.isFinite(tenureMonths) || tenureMonths <= 0) return undefined;
  if (!Number.isFinite(annualRatePercent) || annualRatePercent < 0) return undefined;

  // Zero-interest edge case: EMI is simply principal / tenure.
  if (annualRatePercent === 0) {
    return principal / tenureMonths;
  }

  const monthlyRate = annualRatePercent / 12 / 100;
  const factor = Math.pow(1 + monthlyRate, tenureMonths);
  const emi = (principal * monthlyRate * factor) / (factor - 1);

  if (!Number.isFinite(emi) || emi <= 0) return undefined;
  return emi;
}

/**
 * Applies the loan's configured EMI rounding rule to a raw calculated
 * EMI. Reuses the existing `EmiRoundingRule` type/field from Phase 1's
 * Loan record (src/types/loan.ts) — this is not a new LIC-specific
 * assumption, just applying the one Phase 1 already defined.
 */
export function applyEmiRounding(rawEmi: number, rule: EmiRoundingRule): number {
  switch (rule) {
    case 'nearest_paisa':
      return Math.round(rawEmi * 100) / 100;
    case 'nearest_rupee':
      return Math.round(rawEmi);
    case 'nearest_ten_rupees':
      return Math.round(rawEmi / 10) * 10;
  }
}

/**
 * Inverse of calculateEmi(): solves for TENURE given principal, rate, and
 * a target EMI. Standard closed-form inverse of the reducing-balance EMI
 * formula:
 *
 *   n = -ln(1 - P·r/EMI) / ln(1+r)
 *
 * where P = principal, r = monthly rate, EMI = target monthly payment.
 *
 * Returns `undefined` if the inputs can't produce a finite tenure — in
 * particular, if EMI is too small to even cover the first month's
 * interest (P·r >= EMI), no finite number of months would ever amortize
 * the loan, since the balance would grow instead of shrink. Callers must
 * treat `undefined` the same way as calculateEmi()'s `undefined` — "not
 * enough information to calculate", never coerced to 0 or Infinity.
 *
 * The raw result is generally fractional (e.g. 187.34 months). This
 * function returns the RAW fractional value — full internal precision is
 * preserved here deliberately, since rounding up too early would lose
 * precision for any further chained calculation. Callers that need a
 * whole-month tenure for display or for a final schedule's closing
 * period should round UP (Math.ceil) at the point of use, and are
 * expected to handle the resulting partial final EMI the same way the
 * engine's schedule generation already does (a smaller final payment
 * that exactly zeroes the balance) — see src/lib/calc/engine.ts.
 */
export function calculateTenureMonthsRaw(
  principal: number,
  annualRatePercent: number,
  emi: number
): number | undefined {
  if (!Number.isFinite(principal) || principal <= 0) return undefined;
  if (!Number.isFinite(emi) || emi <= 0) return undefined;
  if (!Number.isFinite(annualRatePercent) || annualRatePercent < 0) return undefined;

  // Zero-interest edge case: tenure is simply principal / EMI.
  if (annualRatePercent === 0) {
    return principal / emi;
  }

  const monthlyRate = annualRatePercent / 12 / 100;

  // EMI must exceed the first period's interest, or the loan never
  // amortizes (balance would grow without bound).
  const firstPeriodInterest = principal * monthlyRate;
  if (emi <= firstPeriodInterest) return undefined;

  const ratio = 1 - (principal * monthlyRate) / emi;
  // ratio is guaranteed in (0, 1) here given the guard above, so log is safe.
  const n = -Math.log(ratio) / Math.log(1 + monthlyRate);

  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

/**
 * Convenience wrapper returning a whole-month tenure (rounded UP, per
 * the approved convention: a fractional final month becomes one extra,
 * smaller, closing payment rather than being rounded off). Use
 * calculateTenureMonthsRaw() directly when full precision needs to be
 * threaded into a further calculation (e.g. the engine's own schedule
 * generation, which handles the fractional final period itself and
 * would otherwise double-round).
 */
export function calculateTenureMonths(
  principal: number,
  annualRatePercent: number,
  emi: number
): number | undefined {
  const raw = calculateTenureMonthsRaw(principal, annualRatePercent, emi);
  if (raw === undefined) return undefined;
  return Math.ceil(raw);
}

/**
 * Convenience wrapper combining calculation + rounding, used directly by
 * the loan setup form. Returns undefined under the same conditions as
 * calculateEmi().
 */
export function calculateRoundedEmi(
  principal: number,
  annualRatePercent: number,
  tenureMonths: number,
  roundingRule: EmiRoundingRule
): number | undefined {
  const raw = calculateEmi(principal, annualRatePercent, tenureMonths);
  if (raw === undefined) return undefined;
  return applyEmiRounding(raw, roundingRule);
}
